import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Database,
  ExternalLink,
  KeyRound,
  LogOut,
  MonitorSmartphone,
  Moon,
  Server,
  Settings,
  QrCode,
  Smartphone,
  Sun,
  SunMoon,
  UserRound,
  SquareTerminal,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { Avatar } from '@/shared/ui/Avatar';
import { octoLabUrl } from '@/shared/lib/octoLab';
import { useServerInfo } from '../api/shellInfoApi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { useAuthStore } from '@/entities/session/model/authStore';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import type { Preferences } from '@/entities/preferences/model/schema';
import { deviceName } from '@/entities/session/lib/deviceIdentity';
import { openAccountTab } from '@/features/account/accountTab';
import { ChangePasswordDialog } from '@/features/auth/changePassword/ChangePasswordDialog';
import { MobileAppQrDialog } from './MobileAppQrDialog';
import { useShellStore } from '../model/shellStore';
import { runtimeConfig } from '@/app/config/runtimeConfig';
import { openNodeView } from '@/features/node/model/viewMode';

/**
 * Profil menüsü: kimlik kartı + sık kullanılanlar.
 *
 * İki şey bilinçli olarak burada DEĞİL:
 *   - Tasarım modu: bir kabuk ayarı değil, yılda birkaç kez kullanılan bir araç.
 *     Spotlight komutu olarak duruyor (⌘K → "Tasarım modu").
 *   - SQL Editör / Log Monitör: karşılık gelen sayfalar React'ta henüz yok, menüdeki
 *     öğeler tıklanınca ana sayfaya düşüyordu (ölçüldü). Sayfalar geldiğinde geri
 *     eklenecek. Mobil QR'ın böyle bir sayfası yok — adresi gösteren bir pencere — ve
 *     web menüsündeki yerini koruyor.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const connectionName = useAuthStore((s) => s.connectionName);
  const logout = useAuthStore((s) => s.logout);
  const setSettings = useShellStore((s) => s.setSettings);
  const theme = usePrefsStore((s) => s.prefs.theme);
  const setPrefs = usePrefsStore((s) => s.set);
  const navigate = useNavigate();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [mobileQrOpen, setMobileQrOpen] = useState(false);
  const cfg = runtimeConfig();
  // Sunucu bilgisi yalnız sistem rolünde çekilir; uç diğer rollerde 403 döner.
  // Kanca erken `return`'ten ÖNCE çağrılmak zorunda.
  const serverInfo = useServerInfo(!!user?.isSysRol);
  if (!user) return null;

  const device = deviceName();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="user-menu"
          className="hover:bg-bg-hover focus-visible:ring-ring flex items-center gap-2 rounded-md p-1 focus-visible:ring-2 focus-visible:outline-none"
          aria-label={user.userName}
        >
          <Avatar name={user.userName} imageBase64={user.profileImage} />
          <span className="hidden flex-col items-start leading-tight md:flex">
            <span className="max-w-[10rem] truncate text-sm font-medium">{user.userName}</span>
            <span className="text-fg-muted max-w-[10rem] truncate text-[11px]">{user.roleName}</span>
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        {/* Kimlik kartı: kim olduğun ve HANGİ VERİTABANINDA olduğun. Bağlantı adı
            eskiden başlık satırında "ad · bağlantı" diye sıkışıyordu ve uzun adlarda
            kesiliyordu; çok bağlantılı kurulumda yanlış kiracıda iş yapmamak için bu
            bilginin okunur durması gerekiyor. */}
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar name={user.userName} imageBase64={user.profileImage} className="h-10 w-10 text-sm" />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold">{user.userName}</span>
            <span className="text-fg-muted truncate text-xs">
              {[user.roleName, user.userCode].filter(Boolean).join(' · ')}
            </span>
            {connectionName ? (
              <span className="bg-brand-500/10 text-brand-600 mt-1 inline-flex max-w-full items-center gap-1 self-start truncate rounded-full px-2 py-0.5 text-[11px]">
                <Database className="h-3 w-3 shrink-0" /> <span className="truncate">{connectionName}</span>
              </span>
            ) : null}
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Tema menünün içinde: en sık değiştirilen ayar, bunun için Ayarlar'ı açmak
            gereksiz. Seçenekler menü öğesi olarak yazılıyor ki klavyeyle de gezilsin. */}
        <div className="text-fg-subtle px-2 pt-1 pb-0.5 text-[11px] font-medium">
          {t('prefs.theme.label')}
        </div>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(v) => setPrefs({ theme: v as Preferences['theme'] })}
          className="flex gap-1 px-1 pb-1"
        >
          <DropdownMenuRadioItem value="light">
            <Sun className="h-3.5 w-3.5" /> {t('prefs.theme.light')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="h-3.5 w-3.5" /> {t('prefs.theme.dark')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <SunMoon className="h-3.5 w-3.5" /> {t('prefs.theme.system')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => openAccountTab(t('account.title'))}>
          <UserRound className="h-4 w-4" /> {t('account.title')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setSettings(true)}>
          <Settings className="h-4 w-4" /> {t('shell.user.settings')}
        </DropdownMenuItem>
        {/* Şifre değişikliği artık popup: kullanıcıyı açık sekmelerinden koparan ayrı bir
            sayfaya gitmek gerekmiyor. Zorunlu değişiklik akışı sayfayı kullanmaya devam
            ediyor (kabuk henüz açılmamış oluyor). */}
        <DropdownMenuItem onSelect={() => setPasswordOpen(true)}>
          <KeyRound className="h-4 w-4" /> {t('shell.user.changePassword')}
        </DropdownMenuItem>
        {/* Node görünümü herkese açık — aynı uygulama, aynı oturum; node LİSANSI
            kullanıcı kontenjanı için, görünüme geçişin koşulu değil (web de koşulsuz
            gösteriyor). Web yetkisi varsa AYRI SEKMEDE açılır: masaüstündeki açık
            sekmeler ve yarım kalmış formlar node'a bakarken kaybolmasın. */}
        {/* Mobil uygulama: Octo.Dal adresini karekod olarak gösterir; Octo.Mobile ilk
            açılışta bunu okutup sunucuya bağlanıyor (web menüsüyle aynı yerde). */}
        <DropdownMenuItem onSelect={() => setMobileQrOpen(true)}>
          <QrCode className="h-4 w-4" /> {t('shell.user.mobileQr')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openNodeView(user, () => navigate('/node'))}>
          <MonitorSmartphone className="h-4 w-4" /> {t('shell.user.switchToNode')}
          {user.hasWebLicense ? <ExternalLink className="text-fg-subtle ml-auto h-3.5 w-3.5" /> : null}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => window.open(cfg.docsUrl, '_blank', 'noopener')}>
          <BookOpen className="h-4 w-4" /> {t('shell.user.docs')}
        </DropdownMenuItem>
        {user.isSysRol ? (
          <DropdownMenuItem onSelect={() => window.open(octoLabUrl(), 'octolab')}>
            <Database className="h-4 w-4" /> {t('shell.user.octoLab')}
          </DropdownMenuItem>
        ) : null}
        {user.isSysRol ? (
          <DropdownMenuItem onSelect={() => window.open('/sql-editor', 'sqleditor')}>
            <SquareTerminal className="h-4 w-4" /> {t('shell.user.sqlEditor')}
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />

        {/* Alt bilgi: sürüm ve bu cihazın adı. Cihaz adı denetim kayıtlarına yazılıyor
            (bkz. deviceIdentity); burada görünmese kullanıcı böyle bir ayarın
            varlığından haberdar olmuyordu. */}
        <div className="text-fg-subtle px-2 py-1 text-[11px]">
          <div>
            {t('shell.user.version')}: {cfg.appVersion}
          </div>
          {/* Sunucu/veritabanı adresi yalnız sistem rolüne gösterilir (web de öyle):
              kiracı kullanıcısının altyapıyı bilmesi gerekmiyor. Uç de aynı kısıtı
              uyguluyor, bu yüzden burada sadece çağrı yapılmıyor. */}
          {serverInfo.data ? (
            <div className="mt-0.5 flex items-start gap-1">
              <Server className="mt-[2px] h-3 w-3 shrink-0" />
              {/* Adres TAM gösteriliyor: kısaltılınca sunucu adı ve veritabanı yan yana
                  sığmıyor, "SRV01\OCTO/OCTO_QUA…" gibi kesik bir metin kalıyordu ve
                  hangi veritabanına bağlı olunduğu ancak ipucu bekleyerek anlaşılıyordu. */}
              <span className="break-all">
                {serverInfo.data.server}/{serverInfo.data.database}
              </span>
            </div>
          ) : null}
          <button
            type="button"
            className="text-fg-muted hover:text-fg focus-visible:ring-ring mt-0.5 flex w-full items-center gap-1 truncate rounded text-left hover:underline focus-visible:ring-1 focus-visible:outline-none"
            onClick={() => setSettings(true)}
          >
            <Smartphone className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {device ? `${t('shell.user.thisDevice')}: ${device}` : t('shell.user.nameThisDevice')}
            </span>
          </button>
        </div>

        <DropdownMenuItem onSelect={() => void logout('manual')} className="text-danger">
          <LogOut className="h-4 w-4" /> {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
      <MobileAppQrDialog open={mobileQrOpen} onClose={() => setMobileQrOpen(false)} />
    </DropdownMenu>
  );
}
